import React from 'react';



const IDCard = React.forwardRef(({ user }, ref) => {
    if (!user) {
        return null;
    }

    const profileUrl = `https://healthcard.searchisend.com/member/${user.memberId}`;
    const qrCodeImageSrc = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(profileUrl)}`;

    // For local dev, fall back to UI Avatar if no profileURL. For live site, point to actual URL.
    const profileImageSrc = user.profilePictureUrl
        ? `https://healthcard.searchisend.com${user.profilePictureUrl}`
        : `https://ui-avatars.com/api/?name=${user.firstName}+${user.lastName}&background=059669&color=fff&size=150`;

    return (
        <div ref={ref} style={{ width: '278px', height: '394px' }} className="bg-white rounded-xl shadow-lg flex flex-col font-sans text-brand-secondary overflow-hidden relative border border-slate-200">
            {/* Header Banner - top 15% */}
            <div className="bg-gradient-to-r from-brand-primary to-emerald-400 p-2 text-center text-white relative shrink-0">
                <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiPgo8cmVjdCB3aWR0aD0iOCIgaGVpZ2h0PSI4IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMSI+PC9yZWN0Pgo8cGF0aCBkPSJNMCAwTDggOFpNOCAwTDAgOFoiIHN0cm9rZT0iIzAwMCIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSI+PC9wYXRoPgo8L3N2Zz4=')] opacity-30"></div>
                <h2 className="text-[14px] font-bold tracking-tight uppercase relative z-10 flex items-center justify-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path></svg>
                    Digital Health Card
                </h2>
                <p className="text-[8px] font-semibold tracking-wider text-emerald-50 relative z-10 mt-0.5">EMERGENCY MEDICAL ID</p>
            </div>

            {/* Body - Middle ~75% */}
            <div className="flex flex-col items-center flex-grow bg-gradient-to-b from-white to-slate-50 relative p-3 text-center">

                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-md bg-white -mt-1 z-10 relative bg-slate-100 flex items-center justify-center">
                    <img
                        crossOrigin="anonymous"
                        src={`https://healthcard.searchisend.com/health_api/get_image_proxy.php?url=${encodeURIComponent(profileImageSrc)}`}
                        alt="Profile"
                        className="w-full h-full object-cover"
                    />
                </div>

                <div className="text-center mt-3 w-full px-2">
                    <p className="text-xl font-extrabold text-brand-secondary leading-tight tracking-tight uppercase truncate">
                        {user.firstName} {user.lastName || ''}
                    </p>
                    <p className="text-[11px] font-bold text-emerald-600 uppercase tracking-wider truncate mb-1">{user.profession || 'Member'}</p>
                </div>

                <div className="w-full flex items-end justify-between px-1 mt-auto pb-1 z-10 gap-2">
                    <div className="flex-1 bg-white px-2 py-1.5 text-[10px] rounded-lg shadow-sm border border-slate-100 flex flex-col justify-center gap-1 text-left min-w-0">
                        <div className="flex justify-between items-center border-b border-gray-50 pb-0.5">
                            <span className="font-bold text-slate-400 whitespace-nowrap mr-1">ID:</span>
                            <span className="font-extrabold text-brand-secondary text-[8.5px] leading-tight break-all pl-1 text-right">{user.memberId}</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-gray-50 pb-0.5">
                            <span className="font-bold text-slate-400 whitespace-nowrap mr-1">DOB:</span>
                            <span className="font-bold text-brand-secondary text-[9px] leading-tight text-right whitespace-nowrap">{user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString() : 'N/A'}</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-gray-50 pb-0.5">
                            <span className="font-bold text-slate-400 whitespace-nowrap mr-1">Blood:</span>
                            <span className="font-extrabold text-brand-accent text-[11px] leading-none text-right whitespace-nowrap">{user.bloodGroup || 'N/A'}</span>
                        </div>

                        {/* Emergency Contact Always Showing */}
                        <div className="pt-0.5 mt-0.5 flex flex-col gap-0.5">
                            <span className="font-bold text-red-500 text-[8px] uppercase tracking-wider flex items-center gap-1 whitespace-nowrap">
                                <svg className="w-3 h-3 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path></svg>
                                Emergency
                            </span>
                            <div className="flex justify-between items-start mt-0.5">
                                <span className="font-bold text-slate-700 text-[8px] leading-tight pr-1 text-left break-words flex-1">{user.emergencyContactName || 'N/A'}</span>
                                <span className="font-extrabold text-brand-secondary text-[9px] shrink-0 mt-auto">{user.emergencyContactPhone || 'N/A'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-1 rounded-md shadow-sm border border-slate-200 shrink-0 self-end">
                        <img
                            crossOrigin="anonymous"
                            src={`https://healthcard.searchisend.com/health_api/get_image_proxy.php?url=${encodeURIComponent(qrCodeImageSrc)}`}
                            alt="QR Code URL"
                            className="w-[54px] h-[54px]"
                        />
                    </div>
                </div>
            </div>

            {/* Footer Bar - bottom 10% */}
            <div className="bg-slate-800 p-1 text-center flex-shrink-0 border-t-2 border-brand-accent flex flex-col justify-center min-h-[36px]">
                <p className="text-[9px] text-slate-200 font-bold tracking-wide mb-0.5">Scan QR Code for Medical History</p>
                <div className="flex items-center justify-center gap-1 text-[5.5px]">
                    <span className="text-slate-400 font-bold uppercase tracking-normal">Powered By Search is End</span>
                    <span className="text-slate-600">•</span>
                    <span className="text-brand-primary font-bold uppercase tracking-normal">health.searchisend.com</span>
                </div>
            </div>
        </div>
    );
});

export default IDCard;